import React from 'react';
import { format, parseISO } from 'date-fns';
import { User } from 'lucide-react';

const VIOLET = '#7C3AED';
const durationLabel = (m) => (!m ? '' : m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, '0') : ''}` : `${m} min`);

/* Créneau de cours individuel disponible, affiché dans la vue jour du calendrier Cours. */
export const IndivSlotCard = ({ slot, onClick }) => {
  const start = parseISO(slot.datetime);
  const end = new Date(start.getTime() + (slot.duration_minutes || 60) * 60000);
  const color = slot.employee_color || VIOLET;
  const price = slot.client_price ?? slot.member_price ?? slot.price;
  const original = slot.client_original_price ?? slot.original_member_price ?? slot.original_price;
  const promo = slot.yield_applied && slot.yield_label;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-lg overflow-hidden transition-all active:scale-[0.99] hover:shadow-md"
      style={{ backgroundColor: `${color}15`, borderLeft: `4px solid ${color}` }}
      data-testid={`indiv-slot-card-${slot.id}`}
    >
      <div className="px-3 py-2.5">
        <div className="mb-1 flex items-center gap-1 flex-wrap">
          <User size={12} style={{ color: VIOLET }} />
          <span className="text-[9px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full font-medium">Cours indiv.</span>
          {promo && (
            <span className="text-[9px] uppercase tracking-[0.12em] font-medium px-1.5 py-0.5 rounded-sm"
              style={{ color: '#B8860B', backgroundColor: 'rgba(212, 175, 55, 0.14)', border: '1px solid rgba(212, 175, 55, 0.4)' }}>
              {slot.yield_label}
            </span>
          )}
        </div>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-neutral-900 text-sm leading-tight">
            {slot.course_name?.toLowerCase().startsWith('cours') ? '' : 'Cours '}{slot.course_name}
          </h3>
          {price > 0 && (
            <span className="flex items-baseline gap-1 flex-shrink-0" data-testid={`indiv-slot-price-${slot.id}`}>
              {promo && original > 0 && original !== price && <span className="text-[10px] text-neutral-400 line-through">{original}€</span>}
              <span className="text-xs font-bold text-neutral-800">{price}€</span>
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-600 mt-0.5">
          {format(start, 'HH:mm')} – {format(end, 'HH:mm')}
          {slot.duration_minutes ? <span className="text-neutral-400"> · {durationLabel(slot.duration_minutes)}</span> : null}
        </p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-neutral-500 truncate">{slot.employee_name || ''}</p>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">Réserver</span>
        </div>
      </div>
    </button>
  );
};


/* Version compacte pour la vue semaine du calendrier Cours (mêlée par heure aux cours collectifs). */
export const IndivWeekChip = ({ slot, onClick }) => {
  const color = slot.employee_color || VIOLET;
  const price = slot.client_price ?? slot.member_price ?? slot.price;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full p-2 rounded-lg text-left text-xs transition-all hover:scale-[1.02]"
      style={{ backgroundColor: `${color}15`, borderLeft: `3px solid ${color}` }}
      data-testid={`indiv-week-chip-${slot.id}`}
    >
      <div className="flex items-center gap-1 text-[9px] font-medium text-violet-700"><User size={10} /> Cours indiv.</div>
      <div className="font-medium truncate text-neutral-900">{slot.course_name}</div>
      <div className="text-neutral-500 mt-0.5">
        {format(parseISO(slot.datetime), 'HH:mm')}
        {slot.duration_minutes ? ` · ${durationLabel(slot.duration_minutes)}` : ''}
        {price > 0 ? <span className="font-semibold text-neutral-800"> · {price}€</span> : null}
      </div>
    </button>
  );
};
